import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatPrice, formatDuration } from "@/lib/format";
import ServiceDialog from "@/components/ServiceDialog";
import { EmptyState } from "@/components/common";

export default function ProviderServices() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/provider/services").then(({ data }) => setItems(data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    try {
      if (editing) await api.put(`/provider/services/${editing.id}`, payload);
      else await api.post("/provider/services", payload);
      toast.success(editing ? "Service updated" : "Service added");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try { await api.delete(`/provider/services/${s.id}`); toast.success("Service deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="provider-services">
      <div className="mb-5 flex items-center justify-between">
        <h2 className="font-heading text-xl font-semibold">Your services</h2>
        <button data-testid="add-service-button" onClick={() => { setEditing(null); setOpen(true); }} className="inline-flex h-11 items-center gap-2 rounded-xl bg-sky-600 px-4 text-sm font-semibold text-white hover:bg-sky-700"><Plus className="h-4 w-4" />Add service</button>
      </div>
      {items.length === 0 ? <EmptyState title="No services yet" text="Add a service so customers can book you." testId="services-empty" /> : (
        <div className="grid gap-3 md:grid-cols-2">
          {items.map((s) => (
            <div key={s.id} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5" data-testid={`ps-service-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div><p className="font-heading text-lg font-semibold">{s.name}</p><p className="mt-1 text-sm text-slate-600">{s.description}</p></div>
                <p className="font-heading text-xl font-bold">{formatPrice(s.price)}</p>
              </div>
              <p className="mt-3 flex items-center gap-1 text-xs text-slate-500"><Clock className="h-3.5 w-3.5" />{formatDuration(s.duration_minutes)} · {s.service_type}</p>
              <div className="mt-4 flex gap-2">
                <button data-testid={`edit-service-${s.id}`} onClick={() => { setEditing(s); setOpen(true); }} className="inline-flex h-10 items-center gap-1 rounded-xl border border-slate-300 px-3 text-sm font-semibold text-slate-700"><Pencil className="h-4 w-4" />Edit</button>
                <button data-testid={`delete-service-${s.id}`} onClick={() => remove(s)} className="inline-flex h-10 items-center gap-1 rounded-xl border border-rose-200 px-3 text-sm font-semibold text-rose-600"><Trash2 className="h-4 w-4" />Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <ServiceDialog open={open} onOpenChange={setOpen} initial={editing} onSave={save} />
    </div>
  );
}
